import { TSAirport } from '@pelicargo/types';
import { cmToIn, getVolumeCBM, inCommonWeightBreak, inToCm, kgToLb, lbToKg } from '@pelicargo/utils';

import { trpc, TrpcOutputs } from '../config/trpc';
import { TypesenseClient } from '../hooks/useTypesense';

type Tokens = (string | number | Tokens)[];

// https://stackoverflow.com/a/59906630
type ArrayLengthMutationKeys = 'splice' | 'push' | 'pop' | 'shift' | 'unshift';
type FixedLengthArray<T, L extends number, TObj = [T, ...Array<T>]> = L extends 0
  ? []
  : Pick<TObj, Exclude<keyof TObj, ArrayLengthMutationKeys>> & {
      readonly length: L;
      [I: number]: T;
      [Symbol.iterator]: () => IterableIterator<T>;
    };

type CalcAirport = TSAirport & {
  priority?: boolean;
};

type CalcType = 'number' | 'airports' | 'path' | 'string' | 'error' | 'priceEstimate' | 'trackingInfo';

const CalcTypeMap = {
  number: 1,
  airports: {
    type: 'airports' as const,
    list: [] as CalcAirport[],
  },
  path: {
    type: 'path' as const,
    path: [] as CalcAirport[],
  },
  string: 'abc',
  error: {
    type: 'error' as const,
    errorMessage: 'abc',
  },
  priceEstimate: { type: 'priceEstimate' as const, result: {} as TrpcOutputs['priceEstimate'] },
  trackingInfo: {
    type: 'trackingInfo' as const,
    events: {} as TrpcOutputs['trackAwbAdHoc'],
  },
} satisfies Record<CalcType, any>;

// Think of some eldrich demon that consumes variable arguments
// I'll call him Kyle
// Actual info: this can dynamically consume arguments past the predefined amount. This will be fed in as an additional argument
// I see no use for a left-facing vararg.

// This will cause issues with something like `ap lax ap lax` if you don't filter it yourself. Be careful.
// If you're actually in a position to read this and I'm not around to help, I'm sorry
type VarArgEater = (input: AnyReturnType[], rpos: number) => AnyReturnType[];

type AnyReturnType = (typeof CalcTypeMap)[keyof typeof CalcTypeMap];

type ResolveCalcType<T extends readonly CalcType[], TObj = [T, ...Array<T>]> = Pick<
  TObj,
  Exclude<keyof TObj, ArrayLengthMutationKeys>
> & {
  readonly length: T['length'];
  [Symbol.iterator]: () => IterableIterator<T>;
} & {
  [I in keyof T]: (typeof CalcTypeMap)[T[I]];
};

type CalcOperator<
  LeftEat extends number,
  LeftEatTypes extends FixedLengthArray<CalcType, LeftEat>,
  RightEat extends number,
  RightEatTypes extends FixedLengthArray<CalcType, RightEat>,
> = {
  priority: Priority;
  leftEat: LeftEat;
  leftTypes: LeftEatTypes;
  rightEat: RightEat;
  rightTypes: RightEatTypes;
  func: (
    left: ResolveCalcType<RightEatTypes>,
    right: ResolveCalcType<RightEatTypes>,
    context?: CalcContext,
    extra?: AnyReturnType[],
  ) => Promise<AnyReturnType>;
  varArgEater?: VarArgEater;
};

type CalcContext = {
  typesenseClient: TypesenseClient;
  trpc: ReturnType<(typeof trpc)['useUtils']>['client'];
};

enum Priority { // PEMDAS - though parenthesis are already handled
  Highest, // Exponent
  High, // Multiplication
  Medium, // Division
  Low, // Addition
  Lowest, // Subtraction
  VarArg, // I'm losing my mind
}

const operator = <
  LeftEat extends number,
  LeftEatTypes extends FixedLengthArray<CalcType, LeftEat>,
  RightEat extends number,
  RightEatTypes extends FixedLengthArray<CalcType, RightEat>,
>(
  priority: Priority,
  leftEat: LeftEat,
  leftTypes: LeftEatTypes,
  rightEat: RightEat,
  rightTypes: RightEatTypes,
  func: (
    left: ResolveCalcType<RightEatTypes>,
    right: ResolveCalcType<RightEatTypes>,
    context?: CalcContext,
    extra?: AnyReturnType[],
  ) => Promise<AnyReturnType>,
  varArgEater?: VarArgEater,
): CalcOperator<LeftEat, LeftEatTypes, RightEat, RightEatTypes> => {
  return {
    priority,
    leftEat,
    leftTypes,
    rightEat,
    rightTypes,
    func,
    varArgEater,
  };
};

// Uses some old logic for the old kind of airports
const airportLookup = async (aps: string[], tsClient: TypesenseClient) => {
  // For multiple airlines
  const queries = aps;
  for (const arg of aps) {
    if (!arg || arg.length !== 3) {
      return {
        type: 'error' as const,
        errorMessage: `Bad airport '${arg}'`,
      };
    }
  }
  // Search Typesense for all the specified airports
  const airports = await Promise.all(
    queries.map(async (q: any) => {
      const query = await tsClient.collections('airports').documents().search({
        q,
        query_by: 'airport_id',
      });
      const hit = query?.hits?.[0];
      return hit?.document;
    }),
  );

  if (airports.indexOf(undefined) !== -1) {
    return {
      type: 'error' as const,
      errorMessage: `Unknown airport`,
    };
  }

  return {
    type: 'path' as const,
    path: [
      {
        ...(airports[0] as TSAirport),
        priority: true,
      },
      ...(airports.slice(1) as TSAirport[]),
    ],
  };
};

const priceEater = (input: AnyReturnType[]) => {
  let weight = 0;
  let provider_id = undefined;
  if (typeof input?.[3] === 'string' && Number(input[3])) {
    weight = Number(input?.[3]);
  }
  if (typeof input?.[4] === 'string') {
    provider_id = input?.[4];
  }

  return [weight, provider_id];
};

const priceHandler = (extra: AnyReturnType[]) => {
  const [weight, provider_id] = extra;
  if (weight && typeof weight !== 'number') {
    return {
      type: 'error',
      errorMessage: 'Min weight is not a number',
    };
  }

  if (provider_id && typeof provider_id !== 'string') {
    return {
      type: 'error',
      errorMessage: 'Airline ID is not a string',
    };
  }
  let weightBreak = {
    min: 0,
    max: Number.MAX_SAFE_INTEGER,
  };
  if (weight) {
    weightBreak = inCommonWeightBreak(Number(weight));
  }
  return { weightBreak, provider_id };
};

// The long `function` syntax is so that Typesense has a better time at knowing what goes in and out
export const calcOperators = {
  // Arithmetic
  '+': operator(Priority.Low, 1, ['number'], 1, ['number'], async function (left, right) {
    return left[0] + right[0];
  }),
  '-': operator(Priority.Lowest, 1, ['number'], 1, ['number'], async function (left, right) {
    return left[0] - right[0];
  }),
  '/': operator(Priority.Medium, 1, ['number'], 1, ['number'], async function (left, right) {
    return left[0] / right[0];
  }),
  '//': operator(Priority.Medium, 1, ['number'], 1, ['number'], async function (left, right) {
    return Math.round(left[0] / right[0]);
  }),
  '*': operator(Priority.High, 1, ['number'], 1, ['number'], async function (left, right) {
    return left[0] * right[0];
  }),
  '%': operator(Priority.Medium, 1, ['number'], 1, ['number'], async function (left, right) {
    return left[0] % right[0];
  }),

  // JS prioritizes these very low
  '==': operator(Priority.Lowest, 1, ['number'], 1, ['number'], async function (left, right) {
    return Number(left[0] === right[0]);
  }),
  '!=': operator(Priority.Lowest, 1, ['number'], 1, ['number'], async function (left, right) {
    return Number(left[0] !== right[0]);
  }),

  // Unit conversion
  kg: operator(Priority.Lowest, 0, [], 1, ['number'], async function (_, right) {
    return kgToLb(right[0]);
  }),
  lb: operator(Priority.Lowest, 0, [], 1, ['number'], async function (_, right) {
    return lbToKg(right[0]);
  }),
  in: operator(Priority.Lowest, 0, [], 1, ['number'], async function (_, right) {
    return inToCm(right[0]);
  }),
  cm: operator(Priority.Lowest, 0, [], 1, ['number'], async function (_, right) {
    return cmToIn(right[0]);
  }),

  // Common methods
  cmcbm: operator(Priority.Lowest, 0, [], 3, ['number', 'number', 'number'], async function (_, right) {
    return getVolumeCBM({
      length: right[0],
      width: right[1],
      height: right[2],
      quantity: 1,
    });
  }),
  incbm: operator(Priority.Lowest, 0, [], 3, ['number', 'number', 'number'], async function (_, right) {
    const converted = right.map(inToCm);
    return getVolumeCBM({
      length: converted[0],
      width: converted[1],
      height: converted[2],
      quantity: 1,
    });
  }),

  // airports
  ap: operator(
    Priority.VarArg,
    0,
    [],
    1,
    ['string'],
    async function (_, right, context, extra) {
      return await airportLookup([...right, ...(extra as string[])], context.typesenseClient);
    },
    // Consume airport-looking strings until satisfied
    function (input: AnyReturnType[], rpos: number) {
      const remaining = input.slice(rpos);
      const extra = [];
      for (const value of remaining) {
        if (typeof value === 'string' && value.length === 3) {
          extra.push(value);
        } else {
          break;
        }
      }
      return extra;
    },
  ),

  near: operator(
    Priority.VarArg,
    0,
    [],
    1,
    ['string'],
    async function (_, right, context, extra) {
      const airport: string = right[0];
      const radius = extra?.[0] || 250; // 250mi is the default
      if (airport?.length !== 3) {
        return {
          type: 'error' as const,
          errorMessage: 'Please specify a single airport',
        };
      }
      // Get the initial airport
      const query = await context.typesenseClient.collections('airports').documents().search({
        q: airport,
        query_by: 'airport_id',
      });

      const hit = query?.hits?.[0]?.document as TSAirport;
      if (!hit) {
        return {
          type: 'error' as const,
          errorMessage: `Unable to find airport '${airport}'`,
        };
      }
      const nearQuery = await context.typesenseClient
        .collections('airports')
        .documents()
        .search({
          q: '*',
          query_by: 'airport_id',
          filter_by: `airport_latlng:(${hit.airport_latlng[0]}, ${hit.airport_latlng[1]}, ${radius} mi)`,
          sort_by: `airport_latlng(${hit.airport_latlng[0]}, ${hit.airport_latlng[1]}):asc`,
          limit: 100,
        });

      return {
        type: 'airports' as const,
        list: [{ ...hit, priority: true }, ...(nearQuery?.hits?.map((x) => x.document as TSAirport) ?? [])],
      };
    },
    // Consume a single extra number for a radius
    function (input: AnyReturnType[], rpos: number) {
      const remaining = input?.[rpos];
      if (typeof remaining === 'number') {
        return [remaining];
      }
      return [];
    },
  ),

  // zip code
  zip: operator(
    Priority.VarArg,
    0,
    [],
    1,
    ['string'],
    async function (_, right, context, extra) {
      const country = extra?.length ? right[0] : 'US';
      const code = (extra?.length ? extra[0] : right[0]) as string;

      const { lat, lon } = await context.trpc.getPostalCodeLocation.query({
        country,
        code,
      });

      if (!lat || !lon) {
        return {
          type: 'error' as const,
          errorMessage: `Unknown postal code: ${country} ${code}`,
        };
      }
      const nearQuery = await context.typesenseClient
        .collections('airports')
        .documents()
        .search({
          q: '*',
          query_by: 'airport_id',
          filter_by: `airport_latlng:(${lat}, ${lon}, 500 mi)`,
          sort_by: `airport_latlng(${lat}, ${lon}):asc`,
          limit: 100,
        });

      return {
        type: 'airports' as const,
        list: nearQuery.hits?.map((hit) => hit?.document as TSAirport),
      };
    },
    // Consume next argument if it seems like the first argument is a country
    function (input: AnyReturnType[], rpos: number) {
      const country = input?.[rpos - 1];
      const code = input?.[rpos];

      if (typeof country === 'string' && country.length === 2) {
        return [code];
      }
      return [];
    },
  ),

  price: operator(
    Priority.VarArg,
    0,
    [],
    2,
    ['string', 'string'],
    async function (_, right, context, extra) {
      const { provider_id, weightBreak } = priceHandler(extra);
      return {
        type: 'priceEstimate',
        result: await context.trpc.priceEstimate.query({
          airport_from: right[0],
          airport_to: right[1],
          guess: false,
          min_weight: weightBreak.min,
          max_weight: weightBreak.max,
          // Filter already done
          provider_id: provider_id as string,
        }),
      };
    },
    priceEater,
  ),

  broadprice: operator(
    Priority.VarArg,
    0,
    [],
    2,
    ['string', 'string'],
    async function (_, right, context, extra) {
      const { provider_id, weightBreak } = priceHandler(extra);
      return {
        type: 'priceEstimate',
        result: await context.trpc.priceEstimate.query({
          airport_from: right[0],
          airport_to: right[1],
          guess: true,
          min_weight: weightBreak.min,
          max_weight: weightBreak.max,
          // Filter already done
          provider_id: provider_id as string,
        }),
      };
    },
    priceEater,
  ),

  // awb tracking
  track: operator(Priority.VarArg, 0, [], 1, ['string'], async function (_, right, context) {
    let events: TrpcOutputs['trackAwbAdHoc'];
    try {
      events = await context.trpc.trackAwbAdHoc.mutate({
        awb: right[0],
      });
    } catch (e) {
      return {
        type: 'error' as const,
        errorMessage: e.message,
      };
    }

    return {
      type: 'trackingInfo' as const,
      events,
    };
  }),
};

const dirGuard = (name: string, direction: any[], dirTypes: CalcType[]) => {
  const processed = [];
  for (const i in direction) {
    switch (dirTypes[i] as CalcType) {
      case 'number':
        if (!numberRegex.test(direction[i])) {
          throw Error(`Left argument ${i} on ${name} is not a number, got "${direction[i]}"`);
        }
        processed.push(parseFloat(direction[i]));
        break;
      default:
        processed.push(direction[i]);
      // No airport operators, no need to validate
    }
  }
  return processed;
};

export const guard = (
  name: keyof typeof calcOperators,
  operation: CalcOperator<number, any, number, any>,
  left: AnyReturnType[],
  right: AnyReturnType[],
  context: CalcContext,
  // It's on you to protect an extra, unfortunately
  extra: AnyReturnType[],
) => {
  const leftProcessed = dirGuard(name, left, operation.leftTypes);
  const rightProcessed = dirGuard(name, right, operation.rightTypes);
  // It pains me to write it out, but it has to be done.
  return operation.func(leftProcessed as any, rightProcessed as any, context, extra);
};

const numberRegex = /^-?[0-9.]+$/;

// Mutually exclusive kinds of values. We won't be mixing letters and numbers, so these are points to split on
// Non-matching is "symbol"
const charClasses = [numberRegex, /^[a-zA-Z]+$/];

const tokenize = (input: string, depth = 0) => {
  const tokens: Tokens = [];
  let currentSymbol = '';

  const finishSymbol = () => {
    if (currentSymbol) {
      tokens.push(currentSymbol);

      currentSymbol = '';
    }
  };
  let charI = 0;
  let quoted = false;
  for (; charI < input.length; charI++) {
    // quoted bits are always strings
    if (input[charI] !== '"' && quoted) {
      currentSymbol += input[charI];
      continue;
    }

    switch (input[charI]) {
      case '(':
        {
          finishSymbol();
          const [newTokens, newCharPos] = tokenize(input.substring(charI + 1), depth + 1);
          tokens.push(newTokens);
          charI += newCharPos;
        }
        break;
      case ')':
        finishSymbol();
        return [tokens, charI + 1] as const;
      case ' ':
        if (!quoted) {
          finishSymbol();
        } else {
          currentSymbol += ' ';
        }
        break;
      case '"':
        quoted = !quoted;
        break;
      default: {
        // This can be optimized, but eh
        const curClass = charClasses.findIndex((regex) => regex.test(currentSymbol));
        const charClass = charClasses.findIndex((regex) => regex.test(input[charI]));

        // We love special cases! Negative numbers are the only relevant number modifier, so we need to be able to somewhat
        // account for them. It's a bit hacky but it should't ever come up. Maybe.
        if (input[charI] == '-' && numberRegex.test(input?.[charI + 1])) {
          finishSymbol();
        }
        if (curClass !== charClass && !(currentSymbol === '-' && charClass === 0)) {
          finishSymbol();
        }
        currentSymbol += input[charI];
      }
    }
  }
  finishSymbol();
  return [tokens, charI] as const;
};

const getHighestPriority = (tokens: any[]) => {
  let highest = undefined;
  let highestPriority = Priority.VarArg;
  for (const index in tokens) {
    if (tokens[index] in calcOperators) {
      const priority = calcOperators[tokens[index]].priority;
      if (priority <= highestPriority) {
        highest = index;
        highestPriority = priority;
      }
    }
  }
  return [parseInt(highest), highestPriority];
};

const evaluate = async (
  tokens: (Tokens | AnyReturnType)[],
  context: CalcContext,
): Promise<AnyReturnType[] | AnyReturnType> => {
  if (tokens.length === 1) {
    return tokens[0] as AnyReturnType;
  }

  //After being flattened, it will be this type
  const flattened = structuredClone(tokens) as AnyReturnType[];

  let arrPos = flattened.findIndex(Array.isArray);
  while ((arrPos = flattened.findIndex(Array.isArray)) !== -1) {
    // The cast here sucks, but it is what it is
    flattened[arrPos] = (await evaluate(flattened[arrPos] as unknown as AnyReturnType[], context)) as AnyReturnType;
  }

  let [highest, highestPriority] = getHighestPriority(flattened);
  while (!isNaN(highest)) {
    // Safe cast
    const token = flattened[highest] as keyof typeof calcOperators;
    const operator = calcOperators[token];

    const from = highest - operator.leftEat;
    const to = highest + 1 + operator.rightEat;

    if (highest < operator.leftEat) {
      throw new Error(`Not enough arguments to the left of "${token}"`);
    }
    if (to > flattened.length) {
      throw new Error(`Not enough arguments to the right of "${token}"`);
    }
    const left = flattened.slice(from, highest);
    const right = flattened.slice(highest + 1, highest + 1 + operator.rightEat);
    let extra = [];
    // VarArgs are lowest priority, since we want all operators to be consumed (though varargs may eat other vararg-ers)
    if (highestPriority === Priority.VarArg) {
      const eater = operator?.varArgEater?.(flattened, highest + 1 + operator.rightEat);
      if (eater) {
        extra = eater;
      }
    }

    const resolved = await guard(token, operator, left, right, context, extra);

    flattened.splice(from, to - from + extra.length, resolved as any);
    // Repeat until all solved
    [highest, highestPriority] = getHighestPriority(flattened);
  }
  if (flattened.length === 1) {
    return flattened[0];
  }

  throw new Error(`Unable to parse calculator expression, left with ${tokens}`);
};

export const calculate = async (input: string, context: CalcContext) => {
  const [tokens] = tokenize(input);
  if (!tokens?.length) {
    return null;
  }
  return (await evaluate(tokens, context)) as AnyReturnType;
};
